import _ from "lodash";
import { Avatar, IconButton, Tooltip, useDSContext } from "@toddle-edu/ds-web";
import React from "react";
import DualProfile from "../DualProfile";
import classes from "./SingleUserProfile.scss";
import { AVATAR_COLORS } from "Constants/colorConstants";
import { hashString, isTabletByUserAgent } from "Utils";
import {
  getFormattedUserNameWithoutPronoun,
  getSharedWithString,
  getSharedWithStringV2,
  isPortfolioParityEnabledAndIsJournalRoute,
  isPortfolioRevampAndIsJournalRoute,
} from "Post/utils";
import {
  GlobeOutlined,
  StudentOutlined,
  FamilyOutlined,
  LockOutlined,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import ConditionalWrapper from "Utils/ConditionalWrapper";

const SingleUserProfile = props => {
  const {
    id,
    username = "",
    profileImageSrc = "",
    usernameStyle = {},
    avatarSize = "medium",
    subText = "",
    subtextStyle = {},
    customUserText = null,
    showDualCascadedProfile = false, // if true, then also send following 3 props as well.
    user2 = {}, // send id, firstName, lastName, profileImage as property
    subTextIcon,
    showSharedWith,
    showSharingModal,
    courseTitle,
    shape = "circle",
    showUserNameClassV2 = false,
    cascadeProfileStyle,
    viewCountText = "",
    isCommentItemUser,
    isRightPaneCard,
    isProjectPostCard,
  } = props;

  const { dsVersion } = useDSContext();
  const isDSV2 = dsVersion === "2.0";
  const isTablet = isTabletByUserAgent();

  const getSharedWithIcons = type => {
    switch (type) {
      case "everyone":
        return <GlobeOutlined size={"xxx-small"} variant={"subtle"} />;
      case "student":
        return <StudentOutlined size={"xxx-small"} variant={"subtle"} />;
      case "family":
        return <FamilyOutlined size={"xxx-small"} variant={"subtle"} />;
      case "private":
        return <LockOutlined size={"xxx-small"} variant={"subtle"} />;
      default:
        return null;
    }
  };

  const handleLinkClick = () => {
    showSharingModal(true);
  };

  const sharedWithModal = () => {
    const { isPrivate, isHiddenFromStudents, t } = props;
    const tooltip = getSharedWithString({
      isPrivate,
      isHiddenFromStudents,
      withPrefix: true,
      t,
    });
    return (
      <>
        <div className={classes.iconContainer}>
          <ConditionalWrapper
            type="wrapperAsElement"
            condition={!isTablet}
            wrapper={
              <Tooltip dsVersion="2.0" showArrow={true} tooltip={tooltip} />
            }
          >
            <IconButton
              dsVersion="2.0"
              className={classes.icon}
              onClick={handleLinkClick}
              icon={getSharedWithIcons(
                getSharedWithStringV2({ isPrivate, isHiddenFromStudents })
              )}
              _legacy_variant={"plain"}
              _legacy_size={"xx-small"}
              size="x-small"
              type="plain"
              testId="journal-feed-postcard-sharedwithicon"
            />
          </ConditionalWrapper>
        </div>
        <span className={classes.dotContainer}>
          <span className={classes.dot}></span>
        </span>
      </>
    );
  };

  const oldSubTextClasses = classNames({
    [classes.subText]: !isPortfolioRevampAndIsJournalRoute(),
    ["text-body-s"]: isPortfolioRevampAndIsJournalRoute(),
    [classes.subTextV2]: isPortfolioRevampAndIsJournalRoute(),
  });

  const subTextClasses = classNames({
    [oldSubTextClasses]: !isDSV2,
    [classes.subTextDSV2]: isDSV2,
    "text-body": !isRightPaneCard && isDSV2,
    "text-body-s":
      (isRightPaneCard && isDSV2) || isPortfolioRevampAndIsJournalRoute(),
  });

  const userProfileClasses = classNames({
    [classes.userProfile]: true,
    [classes.userProfileV2]: isDSV2 && !isProjectPostCard,
  });

  const userNameClasses = classNames({
    [classes.userName]: !showUserNameClassV2 && !isDSV2,
    [classes.userNameV2]: isDSV2,
    "text-label-l": !isRightPaneCard && isDSV2,
    "text-label": (isRightPaneCard && isDSV2) || showUserNameClassV2,
  });

  const userAvatarClasses = classNames({
    [classes.userAvatar]: true,
    [classes.userAvatarV2]: isDSV2 && isCommentItemUser,
  });

  return (
    <div className={userProfileClasses}>
      {showDualCascadedProfile ? (
        <div className={classes.userAvatar}>
          <DualProfile
            user1={{
              id: id,
              value: username,
              imageUrl: profileImageSrc,
            }}
            user2={{
              id: _.get(user2, "id"),
              value: isPortfolioParityEnabledAndIsJournalRoute()
                ? getFormattedUserNameWithoutPronoun({
                    user: user2,
                  })
                : `${_.get(user2, "firstName", "")} ${_.get(
                    user2,
                    "lastName",
                    ""
                  )}`,
              imageUrl: _.get(user2, "profileImage", null),
            }}
            showNewDesign={isPortfolioParityEnabledAndIsJournalRoute()}
            shape={shape}
            cascadeProfileStyle={cascadeProfileStyle}
          />
        </div>
      ) : (
        <Avatar
          dsVersion="2.0"
          id={id}
          name={username}
          src={profileImageSrc}
          size={avatarSize}
          shade={"light"}
          className={userAvatarClasses}
          style={{ background: "red" }}
          color={
            isPortfolioParityEnabledAndIsJournalRoute()
              ? AVATAR_COLORS[hashString(id) % AVATAR_COLORS.length]
              : ""
          }
          shape={shape}
        />
      )}

      <div className={classes.userDetails}>
        <div
          data-test-id="journal-feed-postcard-creatordetailswrapper"
          className={userNameClasses}
          style={usernameStyle}
        >
          {!_.isNil(customUserText) ? customUserText : username}
        </div>
        {!_.isEmpty(subText) && (
          <div className={subTextClasses} style={subtextStyle}>
            {!_.isNil(subTextIcon) ? subTextIcon : null}
            {showSharedWith && sharedWithModal()} {subText}
            {courseTitle ? (
              <span
                data-test-id="journal-feed-postcard-time"
                className={classes.courseTitle}
              >
                {" "}
                <span className={classes.dotContainer}>
                  <span className={classes.dot}></span>
                </span>
                {courseTitle}
              </span>
            ) : null}
            {viewCountText ? (
              <span
                data-test-id="journal-feed-postcard-view-count"
                className={classes.viewCountContainer}
              >
                {" "}
                <span className={classes.dotContainer}>
                  <span className={classes.dot}></span>
                </span>
                {viewCountText}
              </span>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleUserProfile;
